import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { Autocomplete } from 'components/FormFields';
import { handleShowError, handleError, generateFilterOptions } from 'utils';
import { useSelector } from 'react-redux';
import { generateTaxTypes } from 'constants';
import { ability } from '../../Can';

const TaxTypesSelect = ({
  label,
  margin,
  variant,
  onChange,
  omitFields,
  asyncErrors,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  ...otherProps
}) => {
  const filterOptions = generateFilterOptions('start', 'label');
  const TRSEnabled = useSelector(
    (state) => state.config.settings?.core?.pricing?.tax_rules_set?.enabled || false
  );
  const allowTaxPerLocation = TRSEnabled && ability.can('read', 'tax_rules_set');
  const taxTypes = generateTaxTypes(allowTaxPerLocation ? [] : ['PERLOCATION']);
  const options = taxTypes.filter((tax) => omitFields.indexOf(tax.value) === -1);

  const handleOptionSelected = (option, value) => option?.value === value?.value || '';
  const handleOnChange = (_, val) => {
    setFieldValue(name, val?.value || '');
    if (onChange) onChange(val?.value || '');
  };

  const innerValue = useMemo(
    () => options.find((tax) => tax?.value == value) || '',
    [options, value]
  );

  return (
    <Autocomplete
      name={name}
      openOnFocus
      label={label}
      margin={margin}
      variant={variant}
      id="tax-types"
      dataCy={otherProps['data-cy'] || name}
      value={innerValue}
      filterOptions={filterOptions}
      error={handleError(name, errors, touched, asyncErrors)}
      onChange={handleOnChange}
      helperText={handleShowError(name, errors, touched, asyncErrors)}
      options={options}
      getOptionSelected={handleOptionSelected}
      getOptionLabel={(option) => (option?.value ? i18n.t(`skus.tax_types.${option?.value}`) : '')}
    />
  );
};

TaxTypesSelect.propTypes = {
  label: PropTypes.string,
  asyncErrors: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  style: PropTypes.object,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  omitFields: PropTypes.array,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

TaxTypesSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
    setFieldValue: () => {},
  },
  style: {},
  field: {},
  omitFields: [],
  label: 'Tax type',
  variant: 'standard',
};

export default TaxTypesSelect;
